import { faArrowDown, faArrowUp, faCheck, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useApi } from "@hooks/useApi";
import useModalManager from "@hooks/useModalManager";
import { Button, CircularProgress } from "@ui";
import { useToastStore } from "components/ToastManager/useToasts";
import { StyledInput } from "@ui/Form";
import axios, { AxiosProgressEvent, CancelTokenSource } from "axios";
import { floor } from "lodash";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MAX_UPLOAD_LABEL } from "./constants";
import { DropzoneFile } from "./FormDropzone";
import ImageHandler from "./ImageHandler";

interface Props {
    file: DropzoneFile;
    error?: any;
    updateFile?: (file: DropzoneFile) => void;
    deleteFile?: (file: DropzoneFile) => void;
    image?: boolean;
    cropperOptions?: Cropper.GetCroppedCanvasOptions;
    aspectRatio?: number;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}

export const FilePreview = ({
    file,
    deleteFile,
    updateFile,
    error,
    cropperOptions,
    aspectRatio,
    image,
    onMoveUp,
    onMoveDown,
}: Props) => {
    const { t } = useTranslation("form");
    const api = useApi();
    const { addModal, removeModal } = useModalManager();
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filename, setFilename] = useState<string>(file.name);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [previewImage, setPreviewImage] = useState<string>("/images/dropzone/eclipse.gif");
    const [uploadStatus, setUploadStatus] = useState(file.id ? 100 : 0);
    const [uploadCancelToken, setUploadCancelToken] = useState<CancelTokenSource>(null);
    const hasStartedRef = useRef(false);
    const isUploaded = file.id || uploadStatus === 100;
    const hasError = uploadStatus === -1;

    const resolveUploadError = (error: any) => {
        // A 413 is produced by API Gateway itself, so it carries no JSON body to read a message from.
        if (error.response?.status === 413) return t("dropzone.tooLarge", { size: MAX_UPLOAD_LABEL });

        return error.response?.data?.message ?? t("dropzone.uploadFailed");
    };

    const uploadFile = (file: Blob, uid: string) => {
        if (!isUploaded && !uploadCancelToken) {
            let cancelToken = axios.CancelToken.source();
            setUploadCancelToken(cancelToken);

            let data = new FormData();
            data.append("file", file);

            axios
                .post(process.env.NEXT_PUBLIC_API_HOST + (image ? "image/upload" : "file/upload"), data, {
                    cancelToken: cancelToken.token,
                    onUploadProgress: (e: AxiosProgressEvent) => {
                        let status = floor((e.loaded / e.total) * 100);
                        status = status > 0 ? status - 1 : status;
                        setUploadStatus(status);
                    },
                })
                .then(({ data }) => {
                    setUploadCancelToken(null);
                    setUploadStatus(100);
                    updateFile({ ...file, ...data.data, uid });
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;

                    setErrorMessage(resolveUploadError(error));
                    setUploadStatus(-1);
                });

            return cancelToken;
        }
    };

    const openImageHandler = useCallback(
        (blob: Blob) => {
            addModal({
                title: t("dropzone.editImage"),
                content: (id) => (
                    <ImageHandler
                        cropOptions={cropperOptions}
                        aspectRatio={aspectRatio}
                        image={blob}
                        onCrop={(croppedBlob) => {
                            const name = file.file.name.replace(/\.[^.]+$/, "") + ".jpg";
                            uploadFile(new File([croppedBlob], name, { type: "image/jpeg" }), file.uid);
                            removeModal(id);
                        }}
                    />
                ),
                show: true,
                size: 50,
            });
        },
        [cropperOptions, aspectRatio, file, uploadFile, addModal, removeModal, t]
    );

    const handleDelete = () => {
        if (uploadCancelToken) {
            uploadCancelToken.cancel();
            deleteFile(file);
        } else if (isUploaded) {
            axios
                .delete(`${process.env.NEXT_PUBLIC_API_HOST}${image ? "image" : "file"}/${file.id}`)
                .then(
                    () => deleteFile(file),
                    (error) => {
                        // guests may not delete on the server; the orphaned upload just stays behind
                        if (error.response?.status === 401) return deleteFile(file);

                        useToastStore.getState().addToast({ type: "error", title: t("dropzone.deleteFailed") });
                    }
                );
        }
    };

    const renderFile = () => {
        if (image) setPreviewImage(`${process.env.NEXT_PUBLIC_API_HOST}image/${file.id}`);
        else if (file.mime_type?.startsWith("image"))
            setPreviewImage(`${process.env.NEXT_PUBLIC_API_HOST}file/${file.id}`);
        else if (file.file?.type?.startsWith("image")) {
            setPreviewImage(URL.createObjectURL(file.file));
            return () => URL.revokeObjectURL(previewImage);
        } else setPreviewImage("/images/dropzone/folder_preview.png");
    };

    useEffect(() => {
        if (!file || !file?.file) return;

        if (!isUploaded && !uploadCancelToken && !hasStartedRef.current) {
            hasStartedRef.current = true;
            if (image) {
                openImageHandler(file.file);
            } else {
                uploadFile(file.file, file.uid);
            }
        }

        return () => {
            if (uploadCancelToken) uploadCancelToken.cancel();
        };
    }, []);

    useEffect(() => {
        renderFile();
    }, [file]);

    const handleEditButton = useCallback(() => {
        setErrorMessage(undefined);
        if (editMode) {
            if (filename === file.name) {
                setEditMode(false);
                return;
            }

            setIsLoading(true);
            api({
                url: `file/${file.id}`,
                method: "PUT",
                data: { name: filename },
                keepSessionOnUnauthorized: true,
                onSuccess: ({ data }) => updateFile({ ...file, ...data }),
                onFinish: () => {
                    setIsLoading(false);
                    setEditMode(false);
                },
                onFailure: () => setErrorMessage("Fehler beim Aktualisieren der Datei."),
            });
        } else {
            setEditMode(true);
        }
    }, [file, filename, editMode]);

    return (
        <div className="tw-card-bordered tw-relative tw-flex tw-items-center tw-gap-4">
            <div className="tw-relative tw-aspect-square tw-h-[60px] tw-overflow-hidden tw-rounded-lg tw-border-r-accents-2">
                {isUploaded ? (
                    <Image
                        src={previewImage}
                        objectFit="cover"
                        layout="fill"
                        alt={t("dropzone.imagePreviewAlt", { name: file.file?.name ?? filename ?? "" })}
                    />
                ) : (
                    <CircularProgress
                        radius={30}
                        withoutText
                        progress={hasError ? 100 : uploadStatus}
                        color={hasError ? "danger" : "success"}
                    />
                )}
            </div>
            <div className="tw-flex tw-flex-1 tw-items-center tw-gap-6">
                <div className="tw-flex-1">
                    {editMode ? (
                        <StyledInput value={filename} onChange={(e) => setFilename(e.currentTarget.value)} />
                    ) : (
                        <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-p-2">
                            {file?.name ?? file.file?.path ?? `Datei #${file.id}`}
                        </span>
                    )}
                    {(error?.id?.message || errorMessage) && (
                        <span className="tw-mt-1 tw-block tw-text-xs tw-text-red-500">
                            {error?.id?.message || errorMessage}
                        </span>
                    )}
                </div>
                {(onMoveUp || onMoveDown) && (
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <Button
                            flat
                            auto
                            size="xs"
                            color="light"
                            type="button"
                            disabled={!onMoveUp}
                            onPress={onMoveUp}
                            aria-label={t("dropzone.moveUp")}
                        >
                            <FontAwesomeIcon icon={faArrowUp} />
                        </Button>
                        <Button
                            flat
                            auto
                            size="xs"
                            color="light"
                            type="button"
                            disabled={!onMoveDown}
                            onPress={onMoveDown}
                            aria-label={t("dropzone.moveDown")}
                        >
                            <FontAwesomeIcon icon={faArrowDown} />
                        </Button>
                    </div>
                )}
                <div className="tw-flex tw-min-w-[70px] tw-flex-col tw-items-center tw-gap-2">
                    <Button
                        flat
                        auto
                        block
                        size="xs"
                        color="error"
                        onPress={handleDelete}
                        disabled={editMode}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                    {!image && (
                        <Button
                            isLoading={isLoading}
                            flat
                            auto
                            type="button"
                            block
                            size="xs"
                            color={editMode ? "success" : "warning"}
                            onPress={handleEditButton}
                        >
                            <FontAwesomeIcon icon={editMode ? faCheck : faEdit} />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
